import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { formatNum, apiErr } from "../lib/utils";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip as RTooltip, CartesianGrid, Legend } from "recharts";
import { Flag, Download, Route } from "lucide-react";
import { toast } from "sonner";

const STATUS_TONE = { green: "success", amber: "warning", red: "danger" };
const STATUS_LABEL = { green: "On track", amber: "At risk", red: "Off track" };

function Stat({ label, value, sub, testid }) {
  return (
    <div className="card-flat p-3" data-testid={testid}>
      <div className="label-eyebrow text-[9px] mb-1">{label}</div>
      <div className="font-mono text-lg font-bold">{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

export function NetZeroRoadmap() {
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/targets/roadmap").then((r) => setD(r.data)).catch((e) => toast.error(apiErr(e)));
  }, []);

  const downloadPdf = async () => {
    try {
      const r = await api.get("/targets/roadmap.pdf", { responseType: "blob" });
      const u = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = u; a.download = "net_zero_roadmap.pdf"; a.click();
      URL.revokeObjectURL(u);
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (!d) return <div className="p-6 text-muted-foreground font-mono text-sm">Loading roadmap…</div>;
  const s = d.summary || {};
  if (!s.has_data || !d.series.length) {
    return (
      <div className="card-flat p-10 text-center text-muted-foreground" data-testid="roadmap-empty">
        No decarbonisation roadmap yet — set emission reduction targets (Environment → Targets) and the year-by-year pathway will appear here.
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="net-zero-roadmap">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <p className="text-xs text-muted-foreground max-w-2xl">
          Actual emissions vs the planned target pathway{s.pathway_scope ? ` (${s.pathway_scope})` : ""}, with a dashed projection
          to net zero at the pathway's final reduction rate.
        </p>
        <Button variant="primary" size="sm" onClick={downloadPdf} data-testid="roadmap-pdf-btn">
          <Download className="h-3.5 w-3.5" /> Board PDF
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Baseline" value={s.baseline_emissions != null ? `${formatNum(s.baseline_emissions)} t` : "—"} sub={s.baseline_year ? `FY${s.baseline_year}` : ""} testid="roadmap-stat-baseline" />
        <Stat label="Latest actual" value={s.latest_emissions != null ? `${formatNum(s.latest_emissions)} t` : "—"} sub={s.latest_year ? `FY${s.latest_year}` : ""} testid="roadmap-stat-latest" />
        <Stat label="Final target" value={s.final_target_emissions != null ? `${formatNum(s.final_target_emissions)} t` : "—"} sub={s.final_target_year ? `FY${s.final_target_year}` : ""} testid="roadmap-stat-target" />
        <Stat label="Projected net zero" value={s.net_zero_year ? `FY${s.net_zero_year}` : "Beyond 2060"} sub="at current pathway rate" testid="roadmap-stat-netzero" />
      </div>

      <div className="card-flat p-4" data-testid="roadmap-chart">
        <ResponsiveContainer width="100%" height={340}>
          <ComposedChart data={d.series} margin={{ left: 8, right: 16, top: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis dataKey="year" tick={{ fontSize: 11, fill: "#888" }} />
            <YAxis tick={{ fontSize: 10, fill: "#888" }} label={{ value: "tCO₂e", angle: -90, position: "insideLeft", fill: "#666", fontSize: 10 }} />
            <RTooltip contentStyle={{ background: "#111", border: "1px solid #333", fontSize: 12 }}
              formatter={(v, n) => [v != null ? `${formatNum(v)} tCO₂e` : "—",
                n === "actual" ? "Actual emissions" : n === "pathway" ? "Planned pathway" : "Net-zero projection"]} />
            <Legend formatter={(v) => v === "actual" ? "Actual emissions" : v === "pathway" ? "Planned pathway" : "Net-zero projection"} wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="actual" fill="#00E65B" radius={[3, 3, 0, 0]} barSize={22} />
            <Line type="monotone" dataKey="pathway" stroke="#FFB800" strokeWidth={2} dot={{ r: 2 }} connectNulls />
            <Line type="monotone" dataKey="projection" stroke="#9CA3AF" strokeWidth={2} strokeDasharray="6 4" dot={false} connectNulls />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="card-flat p-4" data-testid="roadmap-milestones">
        <div className="label-eyebrow mb-2 flex items-center gap-1.5"><Flag className="h-3.5 w-3.5" /> Milestones</div>
        <div className="space-y-2">
          {d.milestones.map((m) => (
            <div key={m.id} className="flex items-center justify-between gap-3 text-xs border-b border-border/50 pb-2 flex-wrap" data-testid="roadmap-milestone-row">
              <div className="min-w-0">
                <span className="font-medium">{m.name}</span>
                {m.sbti_aligned && <Badge variant="outline" className="ml-2 text-[8px]">SBTi</Badge>}
                {!m.emissions && <Badge variant="outline" className="ml-2 text-[8px]">non-emissions</Badge>}
              </div>
              <div className="flex items-center gap-3 font-mono text-[11px] text-muted-foreground shrink-0">
                <span>FY{m.baseline_year}: {formatNum(m.baseline_value)}</span>
                <Route className="h-3 w-3" />
                <span className="text-foreground">FY{m.target_year}: {formatNum(m.target_value)} {m.unit || ""}</span>
                {m.target_reduction_pct != null && <span title="Total reduction the target requires from its baseline">({m.target_reduction_pct}% reduction required)</span>}
                {m.status && <Badge variant={STATUS_TONE[m.status]} className="text-[8px]">{STATUS_LABEL[m.status]}</Badge>}
              </div>
            </div>
          ))}
          {!d.milestones.length && <div className="text-muted-foreground text-xs">No targets yet.</div>}
        </div>
      </div>
    </div>
  );
}
